import {AfterViewInit, Component, computed, Signal} from '@angular/core';
import {SideNavService} from '../../services/side-nav.service';
import {animate, style, transition, trigger} from '@angular/animations';
import {Organization, Query} from '../../types-repository';
import {PortalIdpService} from '../../services/portal-idp.service';
import {ContextService} from '../../services/context.service';
import {OAuthService} from 'angular-oauth2-oidc';
import {UserProfileService} from '../../services/user-profile.service';
import {MonitoringAccessService} from '../../services/monitoring-access.service';
import {environment} from '../../../environments/environment';
import {filter, map, take} from 'rxjs/operators';
import {combineLatest, defer, of} from 'rxjs';
import {AutoLogoutComponent} from '../auto-logout/auto-logout.component';
import {FooterComponent} from '../footer/footer.component';
import {NavigationEnd, Router, RouterModule} from '@angular/router';
import {IsAllowedAnywherePipe, IsAllowedPipe} from '../../pipes/has-action-pipe';

import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BannerOutlet} from '../../banner/banner.component';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';
import {LogoAreaComponent} from '../logo-area/logo-area.component';
import {Airline} from '../../types-workflow';
import {CS, NAV} from '@castlabs/ui';
import {CldatePipe} from '../../pipes/cldate.pipe';
import {GET_ORGANIZATIONS} from '../../queries-repository';
import {Apollo} from 'apollo-angular';
import {toSignal} from '@angular/core/rxjs-interop';
import {WhatsNewService} from '../../services/whats-new.service';

@Component({
  selector: 'app-logged-in',
  templateUrl: './logged-in.component.html',
  styleUrls: ['./logged-in.component.scss'],
  animations: [trigger('flyInAnimation', [
    transition(':leave', [
      animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)'),
      style({height: 0})
    ]),
    transition(':enter', [
      style({height: 0}),
      animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)'),
    ])
  ])],
  imports: [
    AutoLogoutComponent,
    FooterComponent,
    RouterModule,
    IsAllowedPipe,
    IsAllowedAnywherePipe,
    FormsModule,
    BannerOutlet,
    HeadlineSectionComponent,
    LogoAreaComponent,
    ReactiveFormsModule
]
})
export class LoggedInComponent implements AfterViewInit {
  organization: Signal<Organization>
  organizations: Signal<Organization[] | null>
  airlines: Signal<Airline[]>;

  prod = environment.production

  protected readonly environment = environment;
  protected readonly NAV = NAV;

  navOpened = true;

  csNavEntries = computed(() => {
    const licensedServices = this.contextService.licensedProducts();
    const org = this.organization();
    if (!licensedServices || !org) return null;
    const serviceSet = new Set(licensedServices);
    return Object.values(CS.APPS)
      .filter(app => app.sidenav && app.services.some(s => serviceSet.has(s)))
      .map(app => ({
        id: app.id,
        title: app.title,
        subtitle: app.subtitle,
        url: app.urlManage(environment.env_name.toUpperCase(), org.id),
      }));
  });

  constructor(public oauthService: OAuthService,
              protected sideNavService: SideNavService,
              protected contextService: ContextService,
              public userProfileService: UserProfileService,
              protected portalIdp: PortalIdpService,
              private router: Router,
              protected whatsNew: WhatsNewService,
              protected monitoringAccess: MonitoringAccessService,
              private apollo: Apollo) {
    this.organization = this.contextService.organization
    this.airlines = this.contextService.airlines

    const organizationsQuery$ = apollo.query<Query>({
      query: GET_ORGANIZATIONS
    });

    this.organizations = toSignal(organizationsQuery$.pipe(
      map(res => res.data.organizations.organizations),
      map(orgs => [...orgs].sort((a, b) => a.name.localeCompare(b.name)))
    ), {initialValue: null})

  }

  ngAfterViewInit() {
    // Which organization (if any) can open the monitoring dashboard. Resolved once per session; see
    // MonitoringAccessService for why this cannot be a single policy check.
    this.monitoringAccess.resolve();

    this.sideNavService.open()
    this.sideNavService.isOpen.subscribe(isOpen => this.navOpened = isOpen)

    // Pop the changelog open on login when it is newer than the last time the user
    // saw it (or was never seen). Waits for the first navigation to settle so the
    // popover outlet is merged into the deep-linked URL, not into "/".
    const navigated$ = defer(() => this.router.navigated
      ? of(true)
      : this.router.events.pipe(filter(e => e instanceof NavigationEnd), take(1)));

    combineLatest([this.whatsNew.load(), navigated$])
      .pipe(take(1))
      .subscribe(() => this.showWhatsNewIfUnseen());
  }

  private showWhatsNewIfUnseen() {
    if (!this.whatsNew.hasUnseen()) {
      return;
    }
    // Don't cover a popover that is already up — the organization switcher blocks everything else
    // until an organization is picked, and monitoring may have been opened by the login redirect.
    const outlets = this.router.parseUrl(this.router.url).root.children;
    if (outlets['overlay']) {
      return;
    }
    this.router.navigate([{outlets: {overlay: ['whats-new']}}], {queryParamsHandling: 'preserve'});
  }

  doLogout() {
    sessionStorage.setItem('post-logout-path', this.router.url);
    this.oauthService.logOut({
      client_id: this.oauthService.clientId,
      logout_uri: this.oauthService.redirectUri,
    });
  }


}
